import { useEffect, useMemo, useRef, useState } from "react";
import { useSessionStore } from "../store/sessionStore";
import type { Session, SessionMessage } from "../lib/session";

// SearchModal — Cmd/Ctrl+K spotlight over channel titles + recent
// message contents. Result click → selects the matching channel.
// No scroll-to-message yet (would need message ids surfaced through
// ChatThread); v1 just jumps you to the right channel.

interface Props {
  onClose: () => void;
}

interface ChannelHit {
  kind: "channel";
  channel: Session;
}
interface MessageHit {
  kind: "message";
  channel: Session;
  message: SessionMessage;
  snippet: string;
}
type Hit = ChannelHit | MessageHit;

const MAX_RESULTS = 30;
const SNIPPET_RADIUS = 40;

export function SearchModal({ onClose }: Props) {
  const channels = useSessionStore((s) => s.channels);
  const selectChannel = useSessionStore((s) => s.selectChannel);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo<Hit[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query: show recent channels (not archived) as a starter
      // list so users see something useful while they type.
      return channels
        .filter((c) => !c.archived)
        .slice(0, 10)
        .map<Hit>((channel) => ({ kind: "channel", channel }));
    }
    const out: Hit[] = [];
    // Channel-title hits first — fastest, most navigational.
    for (const c of channels) {
      if (c.title.toLowerCase().includes(q)) {
        out.push({ kind: "channel", channel: c });
        if (out.length >= MAX_RESULTS) return out;
      }
    }
    // Then message-content hits, newest channels first.
    for (const c of channels) {
      for (let i = c.messages.length - 1; i >= 0; i--) {
        const m = c.messages[i];
        const lower = m.content.toLowerCase();
        const idx = lower.indexOf(q);
        if (idx < 0) continue;
        const start = Math.max(0, idx - SNIPPET_RADIUS);
        const end = Math.min(m.content.length, idx + q.length + SNIPPET_RADIUS);
        const snippet = (start > 0 ? "…" : "") +
          m.content.slice(start, end).replace(/\s+/g, " ").trim() +
          (end < m.content.length ? "…" : "");
        out.push({ kind: "message", channel: c, message: m, snippet });
        if (out.length >= MAX_RESULTS) return out;
      }
    }
    return out;
  }, [channels, query]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  function commit(hit: Hit | undefined) {
    if (!hit) return;
    selectChannel(hit.channel.id);
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(results[activeIdx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <input
          ref={inputRef}
          className="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="search channels and messages…"
        />
        <div className="search-results">
          {results.length === 0 ? (
            <div className="search-empty">No matches for "{query}".</div>
          ) : (
            results.map((hit, i) => (
              <button
                key={
                  hit.kind === "channel"
                    ? `c-${hit.channel.id}`
                    : `m-${hit.channel.id}-${hit.message.id}`
                }
                className={`search-result ${i === activeIdx ? "active" : ""}`}
                onClick={() => commit(hit)}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="search-result-kind">
                  {hit.kind === "channel" ? "channel" : "message"}
                </span>
                <span className="search-result-main">
                  <span className="search-result-title">{hit.channel.title}</span>
                  {hit.kind === "message" && (
                    <span className="search-result-snippet">
                      <span className="search-result-author">
                        {hit.message.author ?? hit.message.role}:
                      </span>{" "}
                      {hit.snippet}
                    </span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="search-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
